import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import { authApi } from '../lib/api';
import { LogIn, AlertCircle, X } from 'lucide-react';
import { BubbleBackground } from '../components/BubbleBackground';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { setAuth } = useAuthStore();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Prevent multiple submissions
    if (loading) return;
    setLoading(true);

    try {
      const { data } = await authApi.login({ email, password });
      setError(''); // Only clear error on success
      setAuth(data.token, data.user, data.tenant);
      navigate('/');
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Wystąpił błąd logowania');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-dark min-h-screen flex items-center justify-center relative overflow-hidden">
      <BubbleBackground />

      <div className="max-w-md w-full px-4 relative z-10">
        {/* Logo */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="inline-flex items-center justify-center mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="570 478 320 72" style={{ height: '48px', width: 'auto' }}>
              <defs><style>{`.st1{fill:#67e64c}.st2{fill:#fff}`}</style></defs>
              <g>
                <path d="M592.76 526.57c-.06.55.07 1.11.39 1.58l11.33 16.84 10.7-18.42z" style={{fill:'#259950'}}/>
                <path d="M574.85 500.7c-.06.55.07 1.11.39 1.58l11.33 16.84 10.7-18.42z" style={{fill:'#006aee'}}/>
                <path d="m574.84 500.76 8.88-18.25c.4-.82 1.23-1.34 2.14-1.34h49.36l-7.36 15.13a7.95 7.95 0 0 1-7.14 4.47h-45.88Z" style={{fill:'#44a6ff'}}/>
                <path d="m592.75 526.63 8.88-18.25c.4-.82 1.23-1.34 2.14-1.34h29.73l-7.36 15.13a7.95 7.95 0 0 1-7.14 4.47h-26.26Z" className="st1"/>
                <path d="M677.3 521.43v5.54h-23.12v-29.85h22.56v5.54h-15.69v6.48h13.86v5.37h-13.86v6.91h16.25ZM680.46 515.5c0-6.91 5.33-11.81 12.79-11.81 4.82 0 8.62 2.09 10.28 5.84l-5.16 2.77c-1.24-2.18-3.07-3.16-5.16-3.16-3.37 0-6.01 2.35-6.01 6.35s2.64 6.35 6.01 6.35c2.09 0 3.92-.94 5.16-3.16l5.16 2.81c-1.66 3.67-5.46 5.8-10.28 5.8-7.46 0-12.79-4.9-12.79-11.81ZM705.36 515.5c0-6.91 5.33-11.81 12.62-11.81s12.58 4.9 12.58 11.81-5.29 11.81-12.58 11.81-12.62-4.9-12.62-11.81m18.47 0c0-3.97-2.52-6.35-5.84-6.35s-5.89 2.39-5.89 6.35 2.56 6.35 5.89 6.35 5.84-2.39 5.84-6.35M773.76 513.84v13.14h-6.65v-12.11c0-3.71-1.66-5.42-4.35-5.42-2.99 0-5.08 1.92-5.08 6.01v11.51h-6.65v-12.11c0-3.71-1.58-5.42-4.35-5.42-2.94 0-5.03 1.92-5.03 6.01v11.51H735v-22.94h6.35v2.64c1.71-1.96 4.26-2.99 7.21-2.99 3.2 0 5.93 1.24 7.55 3.75 1.83-2.35 4.82-3.75 8.27-3.75 5.46 0 9.38 3.16 9.38 10.15Z" className="st2"/>
                <path d="M787.1 502.66v7.89h13.82v1.28c0 2.35-1.91 4.26-4.26 4.26h-9.55v10.88h-6.91v-29.85h22.56v1.16a4.39 4.39 0 0 1-4.39 4.39h-11.26ZM806.97 497.12h6.65v25.6c0 2.35-1.91 4.26-4.26 4.26h-2.4v-29.85ZM818.1 515.5c0-6.91 5.33-11.81 12.62-11.81s12.58 4.9 12.58 11.81-5.29 11.81-12.58 11.81-12.62-4.9-12.62-11.81m18.47 0c0-3.97-2.52-6.35-5.84-6.35s-5.88 2.39-5.88 6.35 2.56 6.35 5.88 6.35 5.84-2.39 5.84-6.35M883.95 495.71l-7.64 29.4a2.82 2.82 0 0 1-2.65 1.86h-3.42c-.6 0-1.13-.37-1.34-.94l-4.82-13.31-4.99 13.32c-.21.55-.74.92-1.33.92h-3.43c-1.19 0-2.25-.75-2.66-1.87l-7.6-21.08h3.25c1.81 0 3.42 1.14 4.01 2.85l4.42 12.67 5.29-14.59c.2-.56.74-.94 1.33-.94H866c.6 0 1.14.38 1.34.95l5.13 14.58 4.57-21.02a4.26 4.26 0 0 1 4.01-2.82h2.89Z" className="st1"/>
              </g>
            </svg>
          </div>
          <p className="mt-2 text-text-secondary">Zaloguj się i zarządzaj swoim biznesem e-commerce w jednym miejscu.</p>
        </div>

        {/* Login card */}
        <div className="card animate-slide-up">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="flex items-center p-4 bg-danger/10 border border-danger/30 rounded-xl text-danger animate-scale-in">
                <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0" />
                <span className="text-sm flex-1">{error}</span>
                <button
                  type="button"
                  onClick={() => setError('')}
                  className="ml-2 p-1 rounded-lg hover:bg-danger/20 transition-colors"
                  aria-label="Zamknij komunikat"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            <div>
              <label htmlFor="email" className="label">
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder="Podaj adres email..."
              />
            </div>

            <div>
              <label htmlFor="password" className="label">
                Hasło
              </label>
              <input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
                placeholder="Podaj hasło..."
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <div className="loader w-5 h-5 mr-2" />
                  Logowanie...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <LogIn className="w-5 h-5 mr-2" />
                  Zaloguj się
                </span>
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-border-subtle text-center">
            <Link
              to="/register"
              className="text-sm text-accent-primary hover:text-accent-secondary transition-colors font-medium"
            >
              Zarejestruj nowe konto firmowe
            </Link>
          </div>
        </div>

        {/* Footer */}
        <p className="mt-8 text-center text-xs text-text-primary/70">
          EcomFlow &copy; 2026 - Platforma E-commerce 360
        </p>
      </div>
    </div>
  );
}
